import React, { StrictMode, Suspense, lazy } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/react'
import type { Flags } from '@fs/zion-flags'
import { setAppLocales, essentialLocales } from '@fs/zion-locale'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { Router } from '@fs/zion-router'
import { appPath, sentryDSN } from '@fs/zion-config'
import { AppLayout, colors } from '@fs/zion-ui'
import { getSentryConfig } from '@fs/tree-config'
import App from './App'
import * as serviceWorker from './serviceWorker'
import AncestorsHeader from './components/AncestorsHeader/AncestorsHeader'
import runQualtricsInterceptIIFE from './qualtrics-intercept'
import './locales'
import { useDataContext } from './services/data-service'

// Filter out aria-description warning in development environment
// This warning is outside our control and will be fixed when we upgrade to React 18
// https://familysearch.slack.com/archives/CMLL2NGUF/p1745320619906479
// https://familysearch.slack.com/archives/CMLL2NGUF/p1759247106553329?thread_ts=1759246670.030479&cid=CMLL2NGUF
if (process.env.NODE_ENV === 'development') {
  const originalError = console.error

  console.error = (...args) => {
    const firstArg = typeof args[0] === 'string' ? args[0] : ''
    if (
      firstArg.includes('aria-description') ||
      (firstArg.includes('Invalid aria prop') && args[1] === '`aria-description`')
    ) {
      return
    }
    originalError.apply(console, args) // Show all other errors normally
  }
}

const EmptyLoading = (): JSX.Element => <div />

const AncestorsFooter = lazy(() => import('./components/AncestorsFooter/AncestorsFooter'))

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = (
  process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
) as undefined | Flags

const mockActivities = (
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined
) as undefined | Flags

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    getSentryConfig({
      ignoreErrors: [],
      sampleRate: 0.05,
    })
  )
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

const state = window.SERVER_DATA?.ssrState

// Add any app logic here, languages and translations are configured and available here
const FrontierWrapper = (): JSX.Element => {
  setAppLocales(essentialLocales)
  const {
    store: { CF = false },
  } = useDataContext()

  return (
    <Router basename={base}>
      <Root analytics header={{ hidden: true }} footer={{ hidden: CF, external: true }} theme="day" helpChatbot={!CF}>
        <AppLayout fullWidth backgroundColor={colors.gray03}>
          <section style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <AncestorsHeader />
            <App pid={state?.pid} initialData={state?.initialData} />
          </section>
          <Suspense fallback="">{CF && <AncestorsFooter />}</Suspense>
        </AppLayout>
      </Root>
    </Router>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = (): JSX.Element => {
  return (
    <>
      <StrictMode>
        <FrontierBaseProviders mockFlags={mockFlags} LoadingFallback={EmptyLoading} mockActivities={mockActivities}>
          <FrontierWrapper />
        </FrontierBaseProviders>
      </StrictMode>
      <div id="ZN_cI4OucMtvbHUxSZ" />
    </>
  )
}

if (state) {
  ReactDOM.hydrate(<FrontierRoot />, document.getElementById('root'))
} else {
  ReactDOM.render(
    <Suspense fallback="">
      <FrontierRoot />
    </Suspense>,
    document.getElementById('root')
  )
}

// Inject Qualtrics Site Intercept Website Feedback Code
// NOTE: the page must somewhere have a rendered <div id='ZN_cI4OucMtvbHUxSZ' /> for code injection to work, see above.
runQualtricsInterceptIIFE()

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
